import type { QueryClient, QueryKey } from '@tanstack/react-query';

export type ProjectionConnector = 'hubspot' | 'qonto' | 'digiforma';

export const SOURCE_PROJECTION_QUERY_OPTIONS = {
  staleTime: 30_000,
  refetchOnMount: 'always' as const,
  refetchOnWindowFocus: true,
};

export function projectionConsumerQueryKeys(connector: string, selectedYear: number): QueryKey[] {
  const canonical = connector.trim().toLowerCase() === 'quonto' ? 'qonto' : connector.trim().toLowerCase();
  const shared: QueryKey[] = [
    ['integrations'],
    ['integration-link-candidates'],
    ['integration-links'],
    ['dashboard', selectedYear],
    ['my-week', selectedYear],
    ['data-quality', selectedYear],
    ['ai-insights', selectedYear],
    ['ai-actions', selectedYear],
  ];
  const byConnector: Record<ProjectionConnector, QueryKey[]> = {
    hubspot: [
      ['tab', 'opportunites', selectedYear],
      ['tab', 'clients', selectedYear],
      ['entity', 'clients'],
      ['client360'],
      ['finance-ops', selectedYear],
    ],
    digiforma: [
      ['tab', 'calendrier', selectedYear],
      ['tab', 'missions', selectedYear],
      ['client360'],
    ],
    qonto: [
      ['tab', 'factures', selectedYear],
      ['entity', 'factures'],
      ['tab', 'missions', selectedYear],
      ['revenue-reconciliation', selectedYear],
      ['finance-ops', selectedYear],
      ['qonto-balance'],
      ['qonto-reconcile', selectedYear],
      ['tab', 'clients', selectedYear],
      ['entity', 'clients'],
      ['client360'],
    ],
  };
  const connectorKeys = canonical in byConnector ? byConnector[canonical as ProjectionConnector] : [];
  const unique = new Map<string, QueryKey>();
  [...shared, ...connectorKeys].forEach((queryKey) => unique.set(JSON.stringify(queryKey), queryKey));
  return [...unique.values()];
}

export async function refreshProjectionConsumers(
  queryClient: QueryClient,
  connector: string,
  selectedYear: number,
): Promise<void> {
  const keys = projectionConsumerQueryKeys(connector, selectedYear);
  await Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
}
